// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { css } from '@emotion/css';
import {
  Box,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';

import BatchPermissionsPanel from './BatchPermissionsPanel';
import CustomerAccessPanel from './CustomerAccessPanel';
import type { ScopedCustomer } from '../hooks/useCustomerScope';

// TYPES ---------------------------------------------------------------- //

interface AccessTabContentProps {
  customerIds: number[];
  customers: ScopedCustomer[];
}

type AccessMode = 'features' | 'customers';

// COMPONENT ---------------------------------------------------------------- //

const AccessTabContent: React.FC<AccessTabContentProps> = ({ customerIds, customers }) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const [mode, setMode] = React.useState<AccessMode>('features');

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, value: AccessMode | null) => {
    if (value) setMode(value);
  };

  return (
    <Box className={cls.container}>
      <Box className={cls.header}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="features" sx={{ textTransform: 'none', px: 3 }}>
            Features
          </ToggleButton>
          <ToggleButton value="customers" sx={{ textTransform: 'none', px: 3 }}>
            Rooftops
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {mode === 'features'
            ? 'Manage access to portal features for multiple users'
            : 'Manage rooftop access for multiple users'}
        </Typography>
      </Box>

      {mode === 'features' && <BatchPermissionsPanel customerIds={customerIds} />}
      {mode === 'customers' && <CustomerAccessPanel customerIds={customerIds} customers={customers} />}
    </Box>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  container: css`
    padding: ${theme.spacing(3)};
  `,
  header: css`
    margin-bottom: ${theme.spacing(3)};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default AccessTabContent;
